import { useEffect, useState } from "react";
import api from "./api";

/**
 * Cart helpers — localStorage only, no backend sync.
 * Schema: { items: [{ product_id, quantity }] }
 */
const KEY = "tsc_cart_v1";

function readCart() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { items: [] };
    const c = JSON.parse(raw);
    if (!Array.isArray(c.items)) return { items: [] };
    return c;
  } catch {
    return { items: [] };
  }
}

function writeCart(cart) {
  localStorage.setItem(KEY, JSON.stringify(cart));
  window.dispatchEvent(new Event("tsc:cart-changed"));
}

export function getCart() { return readCart(); }
export function clearCart() { writeCart({ items: [] }); }

export function addToCart(productId, quantity = 1) {
  const c = readCart();
  const idx = c.items.findIndex((i) => i.product_id === productId);
  if (idx >= 0) c.items[idx].quantity += quantity;
  else c.items.push({ product_id: productId, quantity });
  writeCart(c);
}

export function setQuantity(productId, quantity) {
  const c = readCart();
  const idx = c.items.findIndex((i) => i.product_id === productId);
  if (idx >= 0) {
    if (quantity <= 0) c.items.splice(idx, 1);
    else c.items[idx].quantity = quantity;
    writeCart(c);
  }
}

export function removeFromCart(productId) {
  const c = readCart();
  c.items = c.items.filter((i) => i.product_id !== productId);
  writeCart(c);
}

export function cartCount() {
  return readCart().items.reduce((sum, i) => sum + i.quantity, 0);
}

/** React hook: re-renders on cart updates. */
export function useCart() {
  const [cart, setCart] = useState(readCart());
  useEffect(() => {
    const handler = () => setCart(readCart());
    window.addEventListener("tsc:cart-changed", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("tsc:cart-changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);
  return cart;
}

/** Hydrate cart items with current product data + compute totals client-side (display only). */
export async function hydrateCart() {
  const cart = readCart();
  if (cart.items.length === 0) return { items: [], subtotal: 0, delivery_fee: 0, total: 0 };
  const hydrated = [];
  let subtotal = 0;
  let maxDelivery = 0;
  for (const it of cart.items) {
    try {
      const { data: p } = await api.get(`/shop/products/${it.product_id}`);
      if (p.status !== "active") continue;
      const lineTotal = (p.price || 0) * it.quantity;
      subtotal += lineTotal;
      maxDelivery = Math.max(maxDelivery, p.delivery_fee || 0);
      hydrated.push({ ...it, product: p, line_total: lineTotal });
    } catch {
      // product missing — drop it from cart
      removeFromCart(it.product_id);
    }
  }
  return {
    items: hydrated,
    subtotal: Math.round(subtotal * 100) / 100,
    delivery_fee: Math.round(maxDelivery * 100) / 100,
    total: Math.round((subtotal + maxDelivery) * 100) / 100,
  };
}
